import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { useAdminPlans as usePlans, savePlans, togglePlanActive, errorToMessage } from '../../lib/plansClient';
import { 
  PackageIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  CheckIcon,
  XIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
  TagIcon,
  PercentIcon,
  MailIcon,
  CopyIcon,
  SaveIcon,
  EyeIcon,
  SparklesIcon,
  UsersIcon,
  DatabaseIcon,
  ZapIcon,
  ShieldIcon,
  TrendingUpIcon,
  GlobeIcon,
  SmartphoneIcon,
  SettingsIcon,
  CreditCardIcon,
  ClockIcon,
  FilterIcon,
  DownloadIcon,
  Banknote as BanknoteIcon,
  MessageSquareIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Features that are meaningless in "Basit Avans Yönetimi" (simple mode) and
// must therefore be locked/unselected whenever a plan is switched to Simple.
// - role_hierarchy: simple mode auto-routes everyone to Süper Admin, no chain.
// - advance_rules:  simple mode bypasses all limit/eligibility checks.
const SIMPLE_MODE_LOCKED_FEATURES = new Set(['role_hierarchy', 'advance_rules']);
const isFeatureLockedBySimple = (featureId, advanceMode) =>
  advanceMode === 'simple' && SIMPLE_MODE_LOCKED_FEATURES.has(featureId);

// AdvanceCash.io özel özellikler
const AVAILABLE_FEATURES = [
  {
    id: 'new_advance_request',
    name: 'Yeni Avans Talebi',
    description: 'Çalışanların avans talebi oluşturması',
    icon: PlusIcon,
    category: 'core'
  },
  {
    id: 'advance_approval',
    name: 'Avans Onaylama',
    description: 'Yönetici onay süreci',
    icon: CheckIcon,
    category: 'core'
  },
  {
    id: 'finance_approval',
    name: 'Finans Onayı',
    description: 'Mali işler onay ve ödeme süreci',
    icon: CreditCardIcon,
    category: 'core'
  },
  {
    id: 'advance_closing',
    name: 'Avans Kapatma',
    description: 'Masraf girişi ve avans kapatma',
    icon: XIcon,
    category: 'core'
  },
  {
    id: 'expense_reporting',
    name: 'Masraf Raporlama',
    description: 'Detaylı masraf raporu ve fatura yönetimi',
    icon: DatabaseIcon,
    category: 'reporting'
  },
  {
    id: 'pending_approvals',
    name: 'Bekleyen Onaylar',
    description: 'Onay bekleyen taleplerin listesi',
    icon: ClockIcon,
    category: 'workflow'
  },
  {
    id: 'approved_advances',
    name: 'Onaylanan Avanslar',
    description: 'Onaylanmış avans listesi ve takibi',
    icon: CheckIcon,
    category: 'workflow'
  },
  {
    id: 'closed_advances',
    name: 'Kapatılan Avanslar',
    description: 'Kapatılmış avans geçmişi',
    icon: DatabaseIcon,
    category: 'reporting'
  },
  {
    id: 'current_account',
    name: 'Cari Hesap',
    description: 'Kullanıcı bazlı cari hesap takibi',
    icon: TrendingUpIcon,
    category: 'accounting'
  },
  {
    id: 'all_requests',
    name: 'Tüm Talepler',
    description: 'Genel talep listesi ve filtreleme',
    icon: DatabaseIcon,
    category: 'reporting'
  },
  {
    id: 'user_management',
    name: 'Kullanıcı Yönetimi',
    description: 'Personel ekleme, düzenleme, silme',
    icon: UsersIcon,
    category: 'admin'
  },
  {
    id: 'role_hierarchy',
    name: 'Rol Hiyerarşisi',
    description: 'Organizasyon yapısı ve yönetici ilişkileri',
    icon: SettingsIcon,
    category: 'admin'
  },
  {
    id: 'category_management',
    name: 'Kategori Yönetimi',
    description: 'Masraf kategorileri, proje/saha, işletme yönetimi',
    icon: TagIcon,
    category: 'admin'
  },
  {
    id: 'user_permissions',
    name: 'Kullanıcı İzinleri',
    description: 'Kullanıcı bazlı özel izin yönetimi',
    icon: ShieldIcon,
    category: 'admin'
  },
  {
    id: 'role_permissions',
    name: 'Rol İzinleri',
    description: 'Rol bazlı izin şablonları',
    icon: SettingsIcon,
    category: 'admin'
  },
  {
    id: 'advance_rules',
    name: 'Avans Kuralları',
    description: 'Avans limitleri ve iş kuralları',
    icon: SettingsIcon,
    category: 'settings'
  },
  {
    id: 'general_settings',
    name: 'Genel Ayarlar',
    description: 'Şirket bilgileri ve sistem ayarları',
    icon: SettingsIcon,
    category: 'settings'
  },
  {
    id: 'email_notifications',
    name: 'Email Bildirimleri',
    description: 'Otomatik email gönderimi',
    icon: MailIcon,
    category: 'notifications'
  },
  {
    id: 'whatsapp_integration',
    name: 'WhatsApp Entegrasyonu',
    description: 'WhatsApp ile bildirim gönderimi',
    icon: SmartphoneIcon,
    category: 'notifications'
  },
  {
    id: 'bulk_operations',
    name: 'Toplu İşlemler',
    description: 'Toplu silme, güncelleme, onaylama',
    icon: ZapIcon,
    category: 'productivity'
  },
  {
    id: 'advanced_filtering',
    name: 'Gelişmiş Filtreleme',
    description: 'Çoklu filtre ve arama özellikleri',
    icon: FilterIcon,
    category: 'productivity'
  },
  {
    id: 'excel_export',
    name: 'Excel Export',
    description: 'Raporları Excel formatında dışa aktarma',
    icon: DownloadIcon,
    category: 'reporting'
  },
  {
    id: 'pdf_export',
    name: 'PDF Export',
    description: 'Avans ve masraf raporlarını PDF olarak kaydetme',
    icon: DownloadIcon,
    category: 'reporting'
  },
  {
    id: 'dashboard_analytics',
    name: 'Dashboard & Analitik',
    description: 'Görsel dashboard ve istatistikler',
    icon: TrendingUpIcon,
    category: 'analytics'
  },
  {
    id: 'mobile_responsive',
    name: 'Mobil Uyumlu',
    description: 'Mobil cihazlardan tam erişim',
    icon: SmartphoneIcon,
    category: 'access'
  },
  {
    id: 'api_access',
    name: 'API Entegrasyonu',
    description: 'Harici sistemlerle entegrasyon',
    icon: ZapIcon,
    category: 'integration'
  },
  {
    id: 'custom_branding',
    name: 'Özel Markalama',
    description: 'Logo ve renk özelleştirme',
    icon: SparklesIcon,
    category: 'customization'
  },
  {
    id: 'priority_support',
    name: 'Öncelikli Destek',
    description: '24/7 destek ve hızlı yanıt',
    icon: ShieldIcon,
    category: 'support'
  },
  {
    id: 'multi_currency',
    name: 'Çoklu Para Birimi',
    description: 'Farklı para birimleriyle işlem',
    icon: BanknoteIcon,
    category: 'advanced'
  },
  {
    id: 'budget_tracking',
    name: 'Bütçe Takibi',
    description: 'Departman/Proje bazlı bütçe kontrolü',
    icon: TrendingUpIcon,
    category: 'advanced'
  },
  {
    id: 'messaging',
    name: 'Mesajlaşma',
    description: 'Kullanıcılar arası dahili mesajlaşma',
    icon: MessageSquareIcon,
    category: 'communication'
  }
];

const UltraFeatureManagement = () => {
  const [activeTab, setActiveTab] = useState('packages'); // packages, campaigns, invites
  
  // Use new plansClient hook for real-time sync
  const { plans: packages, loading, error: plansError, reload: reloadPlans } = usePlans();
  
  const [campaigns, setCampaigns] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  
  // Package Editor State
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    currency: 'TRY',
    billing_period: 'monthly',
    user_limit: '',
    storage_limit_gb: '',
    api_rate_limit: '',
    features: [],
    marketing_features: [],
    is_active: true,
    is_featured: false,
    trial_days: 14,
    yearly_discount_percent: 20,
    marketing_badge: '',
    marketing_badge_text: ''
  });

  // Campaign Editor State
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    discount_percentage: '',
    applicable_packages: [],
    start_date: '',
    end_date: '',
    is_active: true
  });

  // Invite Code State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    code: '',
    email: '',
    package_id: '',
    trial_days: 30,
    note: ''
  });

  // Campaigns are loaded from backend (see loadCampaigns below).
  useEffect(() => {
    if (plansError) {
      toast.error(`Paketler yüklenemedi: ${plansError}`);
    }
    loadCampaigns();

    const mockInvites = [
      {
        id: 'inv_001',
        code: 'WELCOME2025',
        email: 'ornek@sirket.com',
        package_id: 'professional',
        trial_days: 30,
        status: 'active',
        created_at: new Date().toISOString(),
        used: false
      }
    ];
    setInviteCodes(mockInvites);
  }, [plansError]);

  // ============ Campaign API helpers ============
  const campaignHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('ultra_admin_token') || ''}`,
  });

  const loadCampaigns = async () => {
    try {
      const res = await fetch('/api/ultra-admin/campaigns', { headers: campaignHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (e) {
      console.error('loadCampaigns error', e);
    }
  };

  // Package Functions
  const openPackageModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      // Convert new API format to old form format
      setPackageForm({
        name: pkg.title || pkg.name || '',
        slug: pkg.code || pkg.slug || '',
        description: pkg.description || '',
        price: pkg.price || '',
        currency: pkg.currency || 'TRY',
        billing_period: (pkg.period === 'month' || pkg.billing_period === 'monthly') ? 'monthly' : 'yearly',
        user_limit: pkg.userLimit || pkg.user_limit || '',
        storage_limit_gb: pkg.storageGb || pkg.storage_limit_gb || '',
        api_rate_limit: pkg.apiRatePerDay || pkg.api_rate_limit || '',
        features: pkg.features || [],
        marketing_features: pkg.marketing_features || [],
        is_active: pkg.isActive !== false && pkg.is_active !== false,
        is_featured: pkg.isFeatured || pkg.is_featured || false,
        trial_days: pkg.trialDays || pkg.trial_days || 14,
        yearly_discount_percent:
          pkg.yearlyDiscountPercent ?? pkg.yearly_discount_percent ?? 20,
        marketing_badge: pkg.marketing_badge || '',
        marketing_badge_text: pkg.marketing_badge_text || '',
        advance_mode: (pkg.advance_mode || pkg.advanceMode || 'advanced'),
      });
    } else {
      setEditingPackage(null);
      setPackageForm({
        name: '',
        slug: '',
        description: '',
        price: '',
        currency: 'TRY',
        billing_period: 'monthly',
        user_limit: '',
        storage_limit_gb: '',
        api_rate_limit: '',
        features: [],
        marketing_features: [],
        is_active: true,
        is_featured: false,
        trial_days: 14,
        yearly_discount_percent: 20,
        marketing_badge: '',
        marketing_badge_text: ''
      });
    }
    setShowPackageModal(true);
  };

  const savePackage = async () => {
    if (!packageForm.name || !packageForm.price) {
      toast.error('Paket adı ve fiyatı zorunludur');
      return;
    }

    try {
      // Convert form to new API format
      const newPackage = {
        id: editingPackage?.id || packageForm.slug || Date.now().toString(),
        code: packageForm.slug,
        title: packageForm.name,
        description: packageForm.description || '',
        price: Number(packageForm.price),
        currency: packageForm.currency || 'TRY',
        period: packageForm.billing_period === 'monthly' ? 'month' : 'year',
        userLimit: Number(packageForm.user_limit) || 0,
        storageGb: Number(packageForm.storage_limit_gb) || 0,
        apiRatePerDay: Number(packageForm.api_rate_limit) || 0,
        // Trial days: user-configurable for ALL packages (14-day trial applies
        // to every plan). Preserve whatever admin sets (default 14).
        trialDays: Number(packageForm.trial_days) >= 0 ? Number(packageForm.trial_days) : 14,
        yearlyDiscountPercent:
          Number.isFinite(Number(packageForm.yearly_discount_percent))
            && Number(packageForm.yearly_discount_percent) >= 0
            && Number(packageForm.yearly_discount_percent) <= 90
            ? Number(packageForm.yearly_discount_percent)
            : 20,
        isFeatured: packageForm.is_featured || false,
        isActive: packageForm.is_active !== false,
        features: packageForm.features || [],
        marketing_features: (packageForm.marketing_features || []).filter(s => s && s.trim()),
        marketing_badge: packageForm.marketing_badge || null,
        marketing_badge_text: packageForm.marketing_badge_text || null
      };
      
      // Get all existing packages and update/add the new one
      let updatedPackages = [...packages];
      
      if (editingPackage) {
        // Update existing
        const index = updatedPackages.findIndex(p => p.id === editingPackage.id || p.code === editingPackage.code);
        if (index !== -1) {
          updatedPackages[index] = newPackage;
        } else {
          updatedPackages.push(newPackage);
        }
      } else {
        // Add new
        updatedPackages.push(newPackage);
      }
      
      // Save all packages using new API
      const result = await savePlans({ plans: updatedPackages });
      
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      
      toast.success(editingPackage ? 'Paket güncellendi ✅' : 'Paket oluşturuldu ✅');
      setShowPackageModal(false);
      reloadPlans(); // Reload from API
    } catch (error) {
      console.error('Save package error:', error);
      toast.error(errorToMessage(error));
    }
  };

  const deletePackage = async (packageId) => {
    if (window.confirm('Bu paketi silmek istediğinizden emin misiniz?')) {
      try {
        // Remove package from list
        const updatedPackages = packages.filter(p => p.id !== packageId && p.code !== packageId);
        
        // Save updated list using new API
        const result = await savePlans({ plans: updatedPackages });
        
        if (!result.ok) {
          toast.error(result.error);
          return;
        }
        
        toast.success('Paket silindi ✅');
        reloadPlans(); // Reload from API
      } catch (error) {
        console.error('Delete package error:', error);
        toast.error(errorToMessage(error));
      }
    }
  };

  const toggleFeature = (featureId) => {
    // Simple avans modunda role_hierarchy ve advance_rules anlamsız — kilitli tut.
    if (isFeatureLockedBySimple(featureId, packageForm.advance_mode)) {
      const label = featureId === 'advance_rules' ? 'Avans Kuralları' : 'Rol Hiyerarşisi';
      toast.info(`Basit Avans Yönetimi modunda ${label} kullanılmaz`);
      return;
    }
    const features = packageForm.features.includes(featureId)
      ? packageForm.features.filter(f => f !== featureId)
      : [...packageForm.features, featureId];
    setPackageForm({ ...packageForm, features });
  };

  // Campaign Functions
  const openCampaignModal = (campaign = null) => {
    if (campaign) {
      setCampaignForm(campaign);
    } else {
      setCampaignForm({
        name: '',
        discount_percentage: '',
        applicable_packages: [],
        start_date: '',
        end_date: '',
        is_active: true
      });
    }
    setShowCampaignModal(true);
  };

  const saveCampaign = async () => {
    if (!campaignForm.name || !campaignForm.discount_percentage) {
      toast.error('Kampanya adı ve indirim oranı zorunludur');
      return;
    }
    const pct = Number(campaignForm.discount_percentage);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 90) {
      toast.error('İndirim oranı 1-90 arası olmalı');
      return;
    }

    try {
      const payload = {
        name: campaignForm.name,
        discount_percentage: pct,
        applicable_packages: campaignForm.applicable_packages || [],
        start_date: campaignForm.start_date || null,
        end_date: campaignForm.end_date || null,
        is_active: campaignForm.is_active !== false,
      };
      let res;
      if (campaignForm.id) {
        res = await fetch(`/api/ultra-admin/campaigns/${campaignForm.id}`, {
          method: 'PUT',
          headers: campaignHeaders(),
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch('/api/ultra-admin/campaigns', {
          method: 'POST',
          headers: campaignHeaders(),
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Kampanya kaydedilemedi');
      }
      toast.success(campaignForm.id ? 'Kampanya güncellendi' : 'Kampanya oluşturuldu');
      setShowCampaignModal(false);
      loadCampaigns();
    } catch (e) {
      toast.error(e.message || 'Kampanya kaydedilemedi');
    }
  };

  const toggleCampaignActive = async (campaign) => {
    try {
      const res = await fetch(`/api/ultra-admin/campaigns/${campaign.id}/toggle-active`, {
        method: 'PATCH',
        headers: campaignHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Kampanya durumu güncellendi');
      loadCampaigns();
    } catch (e) {
      toast.error('Durum değiştirilemedi');
    }
  };

  const deleteCampaign = async (campaign) => {
    if (!window.confirm(`"${campaign.name}" kampanyasını silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await fetch(`/api/ultra-admin/campaigns/${campaign.id}`, {
        method: 'DELETE',
        headers: campaignHeaders(),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Kampanya silindi');
      loadCampaigns();
    } catch (e) {
      toast.error('Kampanya silinemedi');
    }
  };

  // Invite Functions
  const generateInviteCode = () => {
    const code = 'INV' + Math.random().toString(36).substr(2, 8).toUpperCase();
    setInviteForm({ ...inviteForm, code });
  };

  const sendInvite = () => {
    if (!inviteForm.email || !inviteForm.package_id) {
      toast.error('Email ve paket seçimi zorunludur');
      return;
    }

    const newInvite = {
      ...inviteForm,
      id: Date.now().toString(),
      status: 'active',
      created_at: new Date().toISOString(),
      used: false
    };

    setInviteCodes([...inviteCodes, newInvite]);
    toast.success('Davetiye oluşturuldu ve email gönderildi');
    setShowInviteModal(false);
  };

  const getFeatureIcon = (featureId) => {
    const feature = AVAILABLE_FEATURES.find(f => f.id === featureId);
    return feature ? feature.icon : PackageIcon;
  };

  const getFeatureName = (featureId) => {
    const feature = AVAILABLE_FEATURES.find(f => f.id === featureId);
    return feature ? feature.name : featureId;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Özellik & Paket Yönetimi</h2>
            <p className="text-gray-600 mt-1">Abonelik paketleri, kampanyalar ve davetiyeler</p>
          </div>
          <div className="flex items-center space-x-3">
            {activeTab === 'packages' && (
              <Button onClick={() => openPackageModal()} className="bg-purple-600 hover:bg-purple-700">
                <PlusIcon className="w-4 h-4 mr-2" />
                Yeni Paket
              </Button>
            )}
            {activeTab === 'campaigns' && (
              <Button onClick={() => openCampaignModal()} className="bg-orange-600 hover:bg-orange-700">
                <TagIcon className="w-4 h-4 mr-2" />
                Yeni Kampanya
              </Button>
            )}
            {activeTab === 'invites' && (
              <Button onClick={() => setShowInviteModal(true)} className="bg-green-600 hover:bg-green-700">
                <MailIcon className="w-4 h-4 mr-2" />
                Davetiye Gönder
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 border-b border-gray-200 mt-6">
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'packages'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('packages')}
          >
            <PackageIcon className="w-4 h-4 inline mr-2" />
            Paketler ({packages.length})
          </button>
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'campaigns'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('campaigns')}
          >
            <TagIcon className="w-4 h-4 inline mr-2" />
            Kampanyalar ({campaigns.length})
          </button>
          <button
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'invites'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('invites')}
          >
            <MailIcon className="w-4 h-4 inline mr-2" />
            Davetiyeler ({inviteCodes.length})
          </button>
        </div>
      </Card>

      {/* Packages Tab */}
      {activeTab === 'packages' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map(pkg => (
            <Card key={pkg.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{pkg.title || pkg.name}</h3>
                    {(pkg.isFeatured || pkg.is_featured) && (
                      <Badge className="bg-yellow-100 text-yellow-700">
                        <SparklesIcon className="w-3 h-3 mr-1" />
                        Öne Çıkan
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{pkg.description}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {(pkg.isActive !== false && pkg.is_active !== false) ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`plan-status-${pkg.code || pkg.slug}`}>
                      <ToggleRightIcon className="w-3 h-3 mr-1" />
                      Aktif
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-500 border border-gray-300" data-testid={`plan-status-${pkg.code || pkg.slug}`}>
                      <ToggleLeftIcon className="w-3 h-3 mr-1" />
                      Pasif
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mb-4 pb-4 border-b">
                <div className="flex items-baseline space-x-2">
                  <span className="text-3xl font-bold text-gray-900">{pkg.currency || '₺'}{pkg.price}</span>
                  <span className="text-gray-500">/ {(pkg.period === 'month' || pkg.billing_period === 'monthly') ? 'ay' : 'yıl'}</span>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Kullanıcı Limiti:</span>
                  <span className="font-medium text-gray-900">
                    {(pkg.userLimit || pkg.user_limit) === -1 ? 'Sınırsız' : (pkg.userLimit || pkg.user_limit)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Depolama:</span>
                  <span className="font-medium text-gray-900">{pkg.storageGb ?? pkg.storage_limit_gb ?? 0} GB</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">API Rate Limit:</span>
                  <span className="font-medium text-gray-900">{pkg.apiRatePerDay ?? pkg.api_rate_limit ?? 0}/gün</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Deneme Süresi:</span>
                  <span className="font-medium text-gray-900">{pkg.trialDays ?? pkg.trial_days ?? 0} gün</span>
                </div>
                {/* Advance Management Mode — Basit / Gelişmiş toggle per plan */}
                <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                  <span className="text-gray-600">Avans Yönetimi:</span>
                  <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" data-testid={`advance-mode-toggle-${pkg.code || pkg.slug}`}>
                    {['simple', 'advanced'].map((m) => {
                      const active = (pkg.advance_mode || pkg.advanceMode || 'advanced') === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${active ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                          onClick={async () => {
                            try {
                              const slug = pkg.code || pkg.slug;
                              const token = localStorage.getItem('ultra_admin_token');
                              await axios.put(
                                `${BACKEND_URL}/api/ultra-admin/packages/${slug}/advance-mode`,
                                { advance_mode: m },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success(`${pkg.name}: ${m === 'simple' ? 'Basit' : 'Gelişmiş'} Avans Yönetimi`);
                              reloadPlans();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Mod güncellenemedi');
                            }
                          }}
                          data-testid={`advance-mode-${m}-${pkg.code || pkg.slug}`}
                        >
                          {m === 'simple' ? 'Basit' : 'Gelişmiş'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">{pkg.features.length} özellik:</p>
                <div className="flex flex-wrap gap-1">
                  {pkg.features.slice(0, 4).map(featureId => (
                    <Badge
                      key={featureId}
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 font-normal"
                      title={getFeatureName(featureId)}
                    >
                      {getFeatureName(featureId)}
                    </Badge>
                  ))}
                  {pkg.features.length > 4 && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      +{pkg.features.length - 4}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => openPackageModal(pkg)}
                  data-testid={`plan-edit-${pkg.code || pkg.slug}`}
                >
                  <EditIcon className="w-4 h-4 mr-1" />
                  Düzenle
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className={(pkg.isActive !== false && pkg.is_active !== false)
                    ? "text-amber-700 hover:bg-amber-50 border-amber-300"
                    : "text-emerald-700 hover:bg-emerald-50 border-emerald-300"}
                  onClick={async () => {
                    const wasActive = (pkg.isActive !== false && pkg.is_active !== false);
                    const confirmMsg = wasActive
                      ? `"${pkg.title || pkg.name}" paketini pasif yapmak istediğinize emin misiniz?\n\n• Kayıt (Başlayın) sayfasında görünmeyecek\n• Yükseltme sayfasında görünmeyecek\n• Bu pakette olan mevcut kullanıcılar kullanmaya devam edecek`
                      : `"${pkg.title || pkg.name}" paketini yeniden aktif yapmak istediğinize emin misiniz?`;
                    if (!window.confirm(confirmMsg)) return;
                    const res = await togglePlanActive(pkg.id);
                    if (res.ok) {
                      toast.success(res.data?.is_active ? 'Paket aktif yapıldı' : 'Paket pasif yapıldı');
                      reloadPlans();
                    } else {
                      toast.error(res.error || 'Durum değiştirilemedi');
                    }
                  }}
                  data-testid={`plan-toggle-${pkg.code || pkg.slug}`}
                  title={(pkg.isActive !== false && pkg.is_active !== false) ? 'Pasif yap' : 'Aktif yap'}
                >
                  {(pkg.isActive !== false && pkg.is_active !== false) ? (
                    <ToggleLeftIcon className="w-4 h-4" />
                  ) : (
                    <ToggleRightIcon className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => deletePackage(pkg.id)}
                  data-testid={`plan-delete-${pkg.code || pkg.slug}`}
                >
                  <TrashIcon className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="grid grid-cols-1 gap-4">
          {campaigns.length === 0 && (
            <Card className="p-8 text-center text-gray-500 border-dashed">
              <p className="text-sm">Henüz kampanya yok. {'"Yeni Kampanya"'} ile bir tane oluşturun ve <strong>uygulanacak paketleri seçin</strong> — Register ve Subscription sayfalarında sadece o paketlerde indirim ve urgency banner çıkacak.</p>
            </Card>
          )}
          {campaigns.map(campaign => (
            <Card key={campaign.id} className="p-6" data-testid={`campaign-card-${campaign.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{campaign.name}</h3>
                    <Badge className="bg-orange-100 text-orange-700">
                      %{campaign.discount_percentage} İndirim
                    </Badge>
                    {campaign.is_active ? (
                      <Badge className="bg-green-100 text-green-700" data-testid={`campaign-status-${campaign.id}`}>Aktif</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700" data-testid={`campaign-status-${campaign.id}`}>Pasif</Badge>
                    )}
                  </div>
                  <div className="flex items-center space-x-6 text-sm text-gray-600 flex-wrap gap-y-1">
                    <span>📅 {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString('tr-TR') : '—'} → {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString('tr-TR') : '—'}</span>
                    <span>📦 {(campaign.applicable_packages && campaign.applicable_packages.length > 0)
                      ? `Uygulanan paketler: ${campaign.applicable_packages.join(', ')}`
                      : '⚠️ Hiçbir paket seçilmedi (uygulanmıyor)'}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button size="sm" variant="outline" onClick={() => openCampaignModal(campaign)} data-testid={`campaign-edit-${campaign.id}`}>
                    <EditIcon className="w-4 h-4 mr-1" />
                    Düzenle
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={campaign.is_active ? 'text-amber-700 border-amber-300' : 'text-emerald-700 border-emerald-300'}
                    onClick={() => toggleCampaignActive(campaign)}
                    data-testid={`campaign-toggle-${campaign.id}`}
                  >
                    {campaign.is_active ? <ToggleLeftIcon className="w-4 h-4" /> : <ToggleRightIcon className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteCampaign(campaign)} data-testid={`campaign-delete-${campaign.id}`}>
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Invites Tab */}
      {activeTab === 'invites' && (
        <div className="grid grid-cols-1 gap-4">
          {inviteCodes.map(invite => (
            <Card key={invite.id} className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <code className="px-3 py-1 bg-gray-100 rounded font-mono text-lg font-bold text-purple-600">
                      {invite.code}
                    </code>
                    {invite.used ? (
                      <Badge className="bg-gray-100 text-gray-700">Kullanıldı</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Aktif</Badge>
                    )}
                  </div>
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    <span>📧 {invite.email}</span>
                    <span>📦 {packages.find(p => p.id === invite.package_id)?.name}</span>
                    <span>⏱️ {invite.trial_days} gün deneme</span>
                    <span>📅 {new Date(invite.created_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(invite.code);
                      toast.success('Kod kopyalandı');
                    }}
                  >
                    <CopyIcon className="w-4 h-4 mr-1" />
                    Kopyala
                  </Button>
                  <Button size="sm" variant="outline">
                    <MailIcon className="w-4 h-4 mr-1" />
                    Tekrar Gönder
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Package Editor Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingPackage ? 'Paket Düzenle' : 'Yeni Paket Oluştur'}
                </h3>
                <button onClick={() => setShowPackageModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paket Adı *
                  </label>
                  <Input
                    value={packageForm.name}
                    onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                    placeholder="Örn: Professional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug (URL-friendly)
                  </label>
                  <Input
                    value={packageForm.slug}
                    onChange={(e) => setPackageForm({ ...packageForm, slug: e.target.value })}
                    placeholder="Örn: professional"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama
                </label>
                <Input
                  value={packageForm.description}
                  onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                  placeholder="Paketi tanımlayan kısa açıklama"
                />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fiyat *
                  </label>
                  <Input
                    type="number"
                    value={packageForm.price}
                    onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })}
                    placeholder="29"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Para Birimi
                  </label>
                  <select
                    value={packageForm.currency}
                    onChange={(e) => setPackageForm({ ...packageForm, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="TRY">₺ TRY</option>
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dönem
                  </label>
                  <select
                    value={packageForm.billing_period}
                    onChange={(e) => setPackageForm({ ...packageForm, billing_period: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="monthly">Aylık</option>
                    <option value="yearly">Yıllık</option>
                  </select>
                </div>
              </div>

              {/* Limits */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kullanıcı Limiti
                  </label>
                  <Input
                    type="number"
                    value={packageForm.user_limit}
                    onChange={(e) => setPackageForm({ ...packageForm, user_limit: e.target.value })}
                    placeholder="-1 (sınırsız)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Depolama (GB)
                  </label>
                  <Input
                    type="number"
                    value={packageForm.storage_limit_gb}
                    onChange={(e) => setPackageForm({ ...packageForm, storage_limit_gb: e.target.value })}
                    placeholder="10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Rate Limit
                  </label>
                  <Input
                    type="number"
                    value={packageForm.api_rate_limit}
                    onChange={(e) => setPackageForm({ ...packageForm, api_rate_limit: e.target.value })}
                    placeholder="1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deneme (gün)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={packageForm.trial_days ?? 0}
                    onChange={(e) => setPackageForm({ ...packageForm, trial_days: e.target.value })}
                    placeholder="14"
                    data-testid="package-form-trial-days"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tüm planlarda 14 gün ücretsiz deneme aktif. Farklı bir süre
                    (örn. 7 veya 30) belirlemek istiyorsanız buradan değiştirin.
                    0 girerseniz deneme süresiz olur.
                  </p>
                  <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-2" data-testid="trial-days-existing-notice">
                    🛡️ <strong>Mevcut aboneler etkilenmez.</strong> Buradaki
                    değişiklik SADECE yeni kayıtları etkiler; mevcut abonelerin
                    deneme bitiş tarihi, kayıt anındaki değerle sabit kalır.
                  </p>
                </div>
              </div>

              {/* Yıllık İndirim satırı — Ultra Admin bu paket için yıllık ödemede
                  uygulanan indirim yüzdesini belirler (0–90 arası). 0 → indirim yok. */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Yıllık Ödeme İndirimi (%)
                </label>
                <div className="flex items-center space-x-3">
                  <Input
                    type="number"
                    min="0"
                    max="90"
                    step="1"
                    value={packageForm.yearly_discount_percent ?? 20}
                    onChange={(e) => setPackageForm({ ...packageForm, yearly_discount_percent: e.target.value })}
                    placeholder="20"
                    className="max-w-xs"
                    data-testid="package-form-yearly-discount"
                  />
                  <span className="text-sm text-gray-600">
                    {(() => {
                      const p = Number(packageForm.price) || 0;
                      const d = Math.max(0, Math.min(90, Number(packageForm.yearly_discount_percent) || 0));
                      const monthly = p;
                      const yearlyFull = (p * 12).toFixed(2);
                      const yearlyDiscounted = (p * 12 * (1 - d / 100)).toFixed(2);
                      const savings = (p * 12 - p * 12 * (1 - d / 100)).toFixed(2);
                      return `${packageForm.currency || 'TRY'} ${yearlyFull} → ${yearlyDiscounted} (${savings} tasarruf)`;
                    })()}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Register ve Subscription sayfalarında {'"Yıllık öderseniz %'}{packageForm.yearly_discount_percent || 20}{' indirim"'} olarak gösterilir.
                  0 girerseniz yıllık indirim uygulanmaz.
                </p>
              </div>

              {/* Marketing Badge */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pazarlama Rozeti
                  </label>
                  <select
                    value={packageForm.marketing_badge}
                    onChange={(e) => setPackageForm({ ...packageForm, marketing_badge: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    data-testid="marketing-badge-select"
                  >
                    <option value="">— Yok —</option>
                    <option value="most_popular">🔥 En Popüler</option>
                    <option value="best_value">⭐ En Avantajlı</option>
                    <option value="limited_time">⏰ Sınırlı Süre</option>
                    <option value="new">🆕 Yeni</option>
                    <option value="recommended">👍 Tavsiye Edilen</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">/subscription sayfasında kart üzerinde gösterilir</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Özel Rozet Metni (opsiyonel)
                  </label>
                  <Input
                    value={packageForm.marketing_badge_text}
                    onChange={(e) => setPackageForm({ ...packageForm, marketing_badge_text: e.target.value })}
                    placeholder="Örn: 1 Hafta Erken Kayıt!"
                    data-testid="marketing-badge-text"
                  />
                  <p className="text-xs text-gray-500 mt-1">Varsa hazır rozet yerine bu metin kullanılır</p>
                </div>
              </div>

              {/* Marketing / Landing Feature Bullets — editable free-text list
                  shown on the public /subscription page and Landing plan
                  cards. Independent from the technical `features` catalog. */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Ana Sayfa Özellik Maddeleri ({(packageForm.marketing_features || []).length})
                  </label>
                  <button
                    type="button"
                    onClick={() => setPackageForm({
                      ...packageForm,
                      marketing_features: [...(packageForm.marketing_features || []), '']
                    })}
                    className="px-3 py-1 text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                    data-testid="marketing-features-add-btn"
                  >
                    + Madde Ekle
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Landing sayfası ve /subscription plan kartlarında gösterilen maddeler. Her satır bir bullet. Boş bırakılan satırlar kaydedilirken atlanır.
                </p>
                <div className="space-y-2 max-h-72 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                  {(packageForm.marketing_features || []).length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-4">
                      Henüz madde eklenmedi. "+ Madde Ekle" ile başlayın.
                    </p>
                  )}
                  {(packageForm.marketing_features || []).map((line, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`marketing-feature-row-${idx}`}>
                      <span className="text-xs text-gray-400 w-6 flex-shrink-0">{idx + 1}.</span>
                      <Input
                        value={line}
                        onChange={(e) => {
                          const next = [...packageForm.marketing_features];
                          next[idx] = e.target.value;
                          setPackageForm({ ...packageForm, marketing_features: next });
                        }}
                        placeholder="Örn: Kişisel Dashboard"
                        className="flex-1"
                        data-testid={`marketing-feature-input-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = packageForm.marketing_features.filter((_, i) => i !== idx);
                          setPackageForm({ ...packageForm, marketing_features: next });
                        }}
                        className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Bu maddeyi sil"
                        data-testid={`marketing-feature-delete-${idx}`}
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Özellikler ({packageForm.features.length} seçili)
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      // Basit modda role_hierarchy ve advance_rules'ı hepsini-seç'ten hariç tut.
                      const selectable = packageForm.advance_mode === 'simple'
                        ? AVAILABLE_FEATURES.filter(f => !SIMPLE_MODE_LOCKED_FEATURES.has(f.id))
                        : AVAILABLE_FEATURES;
                      if (packageForm.features.length === selectable.length) {
                        setPackageForm({ ...packageForm, features: [] });
                      } else {
                        setPackageForm({ ...packageForm, features: selectable.map(f => f.id) });
                      }
                    }}
                    className="px-3 py-1 text-sm font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    {(() => {
                      const totalSelectable = packageForm.advance_mode === 'simple'
                        ? AVAILABLE_FEATURES.length - SIMPLE_MODE_LOCKED_FEATURES.size
                        : AVAILABLE_FEATURES.length;
                      return packageForm.features.length === totalSelectable ? '✕ Hepsini Kaldır' : '✓ Hepsini Seç';
                    })()}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto border rounded-lg p-4">
                  {AVAILABLE_FEATURES.map(feature => {
                    const Icon = feature.icon;
                    const isSelected = packageForm.features.includes(feature.id);
                    const isLockedBySimple = isFeatureLockedBySimple(feature.id, packageForm.advance_mode);
                    
                    return (
                      <div
                        key={feature.id}
                        className={`p-3 border-2 rounded-lg transition-all ${
                          isLockedBySimple
                            ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'
                            : isSelected
                              ? 'border-purple-600 bg-purple-50 cursor-pointer'
                              : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                        }`}
                        onClick={() => !isLockedBySimple && toggleFeature(feature.id)}
                        title={isLockedBySimple ? 'Basit Avans Yönetimi modunda kullanılamaz' : undefined}
                        data-testid={`feature-checkbox-${feature.id}`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isSelected && !isLockedBySimple ? 'bg-purple-600' : 'bg-gray-100'
                          }`}>
                            <Icon className={`w-4 h-4 ${isSelected && !isLockedBySimple ? 'text-white' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm text-gray-900">{feature.name}</p>
                              {isSelected && !isLockedBySimple && <CheckIcon className="w-5 h-5 text-purple-600" />}
                              {isLockedBySimple && (
                                <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide">Basit modda kilitli</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{feature.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Toggles */}
              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={packageForm.is_active}
                    onChange={(e) => setPackageForm({ ...packageForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Paket Aktif</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={packageForm.is_featured}
                    onChange={(e) => setPackageForm({ ...packageForm, is_featured: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Öne Çıkan Paket</span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowPackageModal(false)}>
                İptal
              </Button>
              <Button onClick={savePackage} className="bg-purple-600 hover:bg-purple-700">
                <SaveIcon className="w-4 h-4 mr-2" />
                {editingPackage ? 'Güncelle' : 'Oluştur'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Kampanya Oluştur</h3>
                <button onClick={() => setShowCampaignModal(false)} className="text-gray-400">
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kampanya Adı</label>
                <Input
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  placeholder="Örn: Yaz Kampanyası"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">İndirim Oranı (%)</label>
                <Input
                  type="number"
                  value={campaignForm.discount_percentage}
                  onChange={(e) => setCampaignForm({ ...campaignForm, discount_percentage: e.target.value })}
                  placeholder="20"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Başlangıç Tarihi</label>
                  <Input
                    type="date"
                    value={campaignForm.start_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bitiş Tarihi</label>
                  <Input
                    type="date"
                    value={campaignForm.end_date}
                    onChange={(e) => setCampaignForm({ ...campaignForm, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Geçerli Paketler</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-xs text-purple-600 hover:text-purple-800 underline"
                      onClick={() => setCampaignForm({
                        ...campaignForm,
                        applicable_packages: (packages || []).map(p => p.code || p.id).filter(Boolean),
                      })}
                      data-testid="campaign-select-all"
                    >
                      Tümünü Seç
                    </button>
                    <span className="text-gray-300">·</span>
                    <button
                      type="button"
                      className="text-xs text-gray-600 hover:text-gray-800 underline"
                      onClick={() => setCampaignForm({ ...campaignForm, applicable_packages: [] })}
                      data-testid="campaign-select-none"
                    >
                      Temizle
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Kampanya <strong>SADECE seçili paketlere</strong> uygulanır.
                  Hiçbiri seçili değilse kampanya kaydedilse bile hiçbir plana etkisi olmaz.
                </p>
                <div className="space-y-2" data-testid="campaign-packages-list">
                  {packages.map(pkg => (
                    <label key={pkg.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={(campaignForm.applicable_packages || []).includes(pkg.code || pkg.id)}
                        onChange={(e) => {
                          const key = pkg.code || pkg.id;
                          const current = campaignForm.applicable_packages || [];
                          const pkgs = e.target.checked
                            ? [...current, key]
                            : current.filter(p => p !== key);
                          setCampaignForm({ ...campaignForm, applicable_packages: pkgs });
                        }}
                        className="w-4 h-4"
                        data-testid={`campaign-pkg-checkbox-${pkg.code || pkg.id}`}
                      />
                      <span className="text-sm">{pkg.title || pkg.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowCampaignModal(false)}>İptal</Button>
              <Button onClick={saveCampaign} className="bg-orange-600 hover:bg-orange-700" data-testid="campaign-modal-save">Kaydet</Button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Davetiye Gönder</h3>
                <button onClick={() => setShowInviteModal(false)} className="text-gray-400">
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Davetiye Kodu</label>
                <div className="flex space-x-2">
                  <Input
                    value={inviteForm.code}
                    onChange={(e) => setInviteForm({ ...inviteForm, code: e.target.value })}
                    placeholder="INV123ABC"
                  />
                  <Button variant="outline" onClick={generateInviteCode}>
                    <SparklesIcon className="w-4 h-4 mr-2" />
                    Oluştur
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Adresi</label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="ornek@sirket.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Paket</label>
                <select
                  value={inviteForm.package_id}
                  onChange={(e) => setInviteForm({ ...inviteForm, package_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Paket Seçin</option>
                  {packages.map(pkg => (
                    <option key={pkg.id} value={pkg.id}>{pkg.name} (₺{pkg.price}/ay)</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Deneme Süresi (gün)</label>
                <Input
                  type="number"
                  value={inviteForm.trial_days}
                  onChange={(e) => setInviteForm({ ...inviteForm, trial_days: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Not (Opsiyonel)</label>
                <textarea
                  value={inviteForm.note}
                  onChange={(e) => setInviteForm({ ...inviteForm, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                  placeholder="Davetiye ile ilgili notlarınız..."
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  📧 Bu davetiye kodunu kullanan şirket, <strong>{inviteForm.trial_days} gün ücretsiz</strong> deneme süresine sahip olacak.
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowInviteModal(false)}>İptal</Button>
              <Button onClick={sendInvite} className="bg-green-600 hover:bg-green-700">
                <MailIcon className="w-4 h-4 mr-2" />
                Gönder
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraFeatureManagement;
